import type { OperatorRigState } from "@common/utils/operatorRig";
import { FloorNames } from "@common/utils/terrain";
import { CanvasSource, Container, Rectangle, Sprite, Texture } from "pixi.js";
import { PIXI_SCALE } from "./constants";
import { createOperatorGrounding, GROUND_ANCHOR_Y, groundMarkOffset, operatorGroundMaterial, updateGroundMark, updateOperatorGrounding } from "./operatorGrounding";

const CELL = 64;
let sharedFrames: readonly Texture[] | undefined;

/** One immutable 256x64 texture for the whole match: 64 KiB base RGBA.
 * Gradients and paths are baked once, never repainted during movement. */
function frames(): readonly Texture[] {
    if (sharedFrames) return sharedFrames;
    const canvas = document.createElement("canvas");
    canvas.width = CELL * 4; canvas.height = CELL;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not prepare ground contact art");
    const gradient = ctx.createRadialGradient(32, 32, 4, 32, 32, 31);
    gradient.addColorStop(0, "rgba(255,255,255,0.9)");
    gradient.addColorStop(0.45, "rgba(255,255,255,0.65)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gradient; ctx.fillRect(0, 0, CELL, CELL);
    ctx.save(); ctx.translate(CELL, 0);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    for (let i = 0; i < 7; i++) {
        const x = 10 + i * 7;
        ctx.beginPath(); ctx.moveTo(x, 44); ctx.lineTo(x - 7, 22 + i % 3 * 5);
        ctx.quadraticCurveTo(x + 1, 24, x + 5, 43); ctx.fill();
    }
    ctx.restore(); ctx.save(); ctx.translate(CELL * 2, 0);
    ctx.strokeStyle = "rgba(255,255,255,0.6)"; ctx.lineWidth = 3; ctx.lineCap = "round";
    for (let i = 0; i < 4; i++) {
        ctx.beginPath(); ctx.moveTo(15 + i * 7, 42); ctx.quadraticCurveTo(23 + i * 7, 35, 19 + i * 7, 24); ctx.stroke();
    }
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    for (let i = 0; i < 8; i++) ctx.fillRect(9 + i * 6, 20 + i % 3 * 10, 2, 2);
    ctx.restore(); ctx.save(); ctx.translate(CELL * 3, 0);
    ctx.strokeStyle = "rgba(255,255,255,0.8)"; ctx.lineWidth = 3; ctx.lineCap = "round";
    ctx.beginPath(); ctx.ellipse(32, 32, 25, 20, 0, 0.12, 2.48); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(32, 32, 25, 20, 0, 2.98, 5.90); ctx.stroke();
    ctx.lineWidth = 1.5; ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.beginPath(); ctx.ellipse(32, 32, 17, 13, 0, 3.50, 5.70); ctx.stroke(); ctx.restore();
    const source = new CanvasSource({ resource: canvas, scaleMode: "linear", autoGenerateMipmaps: false });
    sharedFrames = Array.from({ length: 4 }, (_, i) => new Texture({ source, frame: new Rectangle(i * CELL, 0, CELL, CELL) }));
    return sharedFrames;
}

/** Three contact shadows and two reusable footfalls per actor. No particles,
 * filters, timers, growing trail lists or per-update geometry/texture uploads. */
export class OperatorGroundContact {
    readonly shadow = new Container();
    readonly effects = new Container();
    private readonly pose = createOperatorGrounding();
    private readonly body: Sprite;
    private readonly feet: readonly Sprite[];
    private readonly marks: readonly { sprite: Sprite; x: number; y: number; at: number; planted: boolean }[];
    private floor?: FloorNames;
    private layer = 0;
    private previousX = 0;
    private previousY = 0;
    constructor() {
        const textures = frames();
        const sprite = (texture: Texture, parent: Container): Sprite => {
            const result = new Sprite(texture); result.anchor.set(0.5); parent.addChild(result); return result;
        };
        this.body = sprite(textures[0], this.shadow);
        this.body.position.set(0, GROUND_ANCHOR_Y * PIXI_SCALE);
        this.body.width = 3.3 * PIXI_SCALE; this.body.height = 1.5 * PIXI_SCALE;
        this.feet = [sprite(textures[0], this.shadow), sprite(textures[0], this.shadow)];
        this.marks = [0, 1].map(() => ({ sprite: sprite(textures[1], this.effects), x: 0, y: 0, at: -Infinity, planted: true }));
        this.shadow.zIndex = -5;
        // Touch the bottom of the boots, under guns and the upper-body overlap.
        this.effects.zIndex = 2;
    }
    update(state: OperatorRigState, parentRotation: number, floor: FloorNames, x: number, y: number, layer: number, now: number): void {
        this.shadow.rotation = this.effects.rotation = -parentRotation;
        updateOperatorGrounding(this.pose, state);
        const material = operatorGroundMaterial(floor);
        const reset = floor !== this.floor || layer !== this.layer || Math.hypot(x - this.previousX, y - this.previousY) > 12;
        const water = floor === FloorNames.Water;
        if (floor !== this.floor) for (const foot of this.feet) {
            // Reuse the foot shadow as a soft water film across the boot edge.
            // Reparent only on a surface change, never as part of a footfall.
            (water ? this.effects : this.shadow).addChild(foot);
        }
        this.floor = floor; this.layer = layer; this.previousX = x; this.previousY = y;
        this.body.tint = material.tint; this.body.alpha = material.opacity * 0.66;
        for (let i = 0; i < 2; i++) {
            const foot = i === 0 ? this.pose.left : this.pose.right, shadow = this.feet[i], mark = this.marks[i];
            shadow.position.set(foot.x * PIXI_SCALE, (foot.y - (water ? 0.12 : 0)) * PIXI_SCALE);
            shadow.width = (1.35 + foot.lift * 0.10) * PIXI_SCALE;
            shadow.height = (0.63 + foot.lift * 0.08) * PIXI_SCALE;
            shadow.tint = water ? 0x53b3bd : material.tint;
            shadow.alpha = (water ? 0.30 : material.opacity) * (1 - foot.lift * 0.72);
            if (updateGroundMark(mark, foot, x, y, state.moving, now, !!material.effect, reset)) {
                mark.sprite.texture = frames()[material.effect === "grass" ? 1 : material.effect === "sand" ? 2 : 3];
                mark.sprite.tint = material.effectTint;
            }
            mark.planted = foot.planted;
            const age = (now - mark.at) / Math.max(1, material.duration);
            const visible = !!material.effect && age >= 0 && age < 1;
            mark.sprite.visible = visible;
            if (!visible) continue;
            mark.sprite.position.set(groundMarkOffset(mark.x, x) * PIXI_SCALE, groundMarkOffset(mark.y, y) * PIXI_SCALE);
            mark.sprite.width = (water ? 1.8 + age * 1.8 : 1.35 + age * 0.20) * PIXI_SCALE;
            mark.sprite.height = (water ? 0.90 + age * 0.90 : 0.76) * PIXI_SCALE;
            mark.sprite.alpha = (water ? 0.65 : 0.55) * (1 - age);
        }
    }
    set visible(value: boolean) { this.shadow.visible = this.effects.visible = value; }
    destroy(): void { this.shadow.destroy({ children: true }); this.effects.destroy({ children: true }); }
}
